import { z } from "zod";
/**
 * `GET /me/notification-preferences` and `PUT` of the same path — the four
 * server-readable facts `D100`'s three messages need and a device token cannot
 * express (`D116`).
 *
 * ── WHY THIS EXISTS AT ALL, GIVEN `D94` ─────────────────────────────────────
 * `D94` refused a row that DUPLICATES "is push on at all": a person with no
 * device token receives nothing, and a second column saying so is the same
 * fact stored twice and free to disagree with itself. Nothing here duplicates
 * it. Three of these fields are per-KIND toggles, which the token cannot
 * express, and the fourth is an HOUR, which it cannot express either. The
 * token stays the master opt-in above all four — `SendPushToAccount` returns
 * `devices: 0` for an account with none, whatever these rows say.
 *
 * ── THE HOUR IS STORED IN UTC, AND THE DEVICE'S OFFSET BESIDE IT ────────────
 * `reminderUtcMinute` is a minute of the UTC day, 0..1439, so the reminder job
 * is `WHERE reminder_enabled = 1 AND reminder_utc_minute = ?` on an index once
 * a minute — no timezone maths inside a `core/model/` that bans `new Date()`,
 * and no new `Schedule` kind (`D116` rejected the IANA-zone alternative for
 * exactly that).
 *
 * The cost is that the stored minute is WRONG the moment the person's offset
 * changes — a DST transition, or a flight. That is what `utcOffsetMinutes` is
 * for: the app reports the device's current offset on every launch, and when
 * it differs from the stored one it PUTs the whole shape back with the minute
 * recomputed from the local hour the person actually chose. So the app owns
 * "20:00 local", the server owns "18:00 UTC", and the rewrite is a client
 * responsibility written down here because there is nothing on the server that
 * could notice.
 *
 * ── PUT REPLACES THE WHOLE SHAPE ────────────────────────────────────────────
 * Every field is required but one; there is no PATCH. `reminderWeekdays` may
 * be left out, and left out means `null` — every day — not "keep what is
 * stored". A partial update of a preference
 * set this small buys nothing and costs a merge rule that has to be identical
 * on both sides. The client GETs, edits and PUTs back.
 */
/** Minutes in a day. `reminderUtcMinute` is `0..MINUTES_PER_DAY - 1`. */
const MINUTES_PER_DAY = 1440;
/**
 * The bound on a device-reported UTC offset, in minutes: ±14:00.
 *
 * SYMMETRIC, and wider than the world is on the negative side. The widest real
 * offsets are −12:00 (Baker Island) and +14:00 (Line Islands); ±840 is one rule
 * instead of two, and the slack costs nothing — a device claiming −13:00 is
 * still bounded arithmetic, and the alternative is a second constant that
 * exists only to refuse a value nobody sends.
 *
 * Bounded rather than free, because this number is arithmetic input on the
 * server — `NotificationPreference.localMorningUtc` shifts a timestamp by it —
 * and an unbounded integer there is a check-in scheduled in the year 30000.
 * The bound is generous on purpose: it admits the half-hour and quarter-hour
 * offsets (India +330, Nepal +345, Chatham +765) that a naive `% 60 === 0`
 * would have refused.
 */
export const UTC_OFFSET_MINUTES_MIN = -840;
export const UTC_OFFSET_MINUTES_MAX = 840;
/** Days in a week. A reminder weekday is `0..DAYS_PER_WEEK - 1`, `0` Sunday. */
const DAYS_PER_WEEK = 7;
/**
 * The days of the week the reminder fires on — `0` Sunday … `6` Saturday, the
 * numbering `training_config.fixed_weekdays` already uses, so a screen can
 * offer "on my training days" by copying one list into the other.
 *
 * ── THE WEEKDAY IS THE PERSON'S **LOCAL** ONE ──────────────────────────────
 * The minute stays UTC (see the file docblock), but "Monday" means Monday on
 * the person's own clock: the per-minute job shifts the instant by the stored
 * `utcOffsetMinutes` before asking which day it is. A reminder at 21:30 in
 * São Paulo is 00:30 UTC the NEXT day, and judging it by the UTC weekday would
 * move every evening reminder west of Greenwich one day late. It inherits the
 * offset's limitation too: a person who travels is judged by the offset the
 * app last PUT until it PUTs again.
 *
 * ── `null`, OR ABSENT, IS EVERY DAY; `[]` IS REFUSED ───────────────────────
 * `null` is the behaviour before this field existed, and absent on a PUT means
 * the same `null` — PUT replaces the whole shape, so leaving it out is choosing
 * every day, not keeping what was stored. An EMPTY list would be "remind me on
 * no day" beside a `reminderEnabled` that says yes: a toggle that reads as on
 * and never fires. That is the half-written form `reminderUtcMinute`'s rule
 * below refuses, and it is refused for the same reason. The way to stop the
 * reminder is `reminderEnabled: false`.
 *
 * Duplicates are refused rather than collapsed, and the order is not kept —
 * the server stores a set and answers it ascending.
 */
const reminderWeekdays = z
    .array(z
    .int()
    .min(0)
    .max(DAYS_PER_WEEK - 1))
    .min(1, {
    error: "reminderWeekdays cannot be empty — a reminder on no day is a toggle that is on and never fires. Send null for every day, or turn reminderEnabled off.",
})
    .max(DAYS_PER_WEEK)
    .refine((days) => new Set(days).size === days.length, {
    error: "reminderWeekdays lists a day twice.",
})
    .nullable()
    .optional();
/**
 * The preference set, as the client sends it and as the server echoes it.
 *
 * `strictObject`, like every other input in this package: a client sending a
 * field this server does not store should be told so rather than have it
 * silently dropped and then wonder where the setting went.
 *
 * ── `reminderEnabled` REQUIRES A MINUTE, AND THAT IS CHECKED HERE ───────────
 * "Remind me" with no hour is not a preference, it is a half-written form. The
 * same rule is restated in `NotificationPreference` — an aggregate may not
 * import this package (arch rule 4), and the aggregate is the only writer, so
 * the wire refusing it is a better error message rather than the protection.
 *
 * The converse is deliberately allowed: a minute WITHOUT `reminderEnabled` is
 * fine and is how the app remembers the hour someone picked while the toggle
 * is off.
 */
export const notificationPreferences = z
    .strictObject({
    /** Is the daily "time to train" reminder on? Requires
     *  {@link notificationPreferences} `reminderUtcMinute`. */
    reminderEnabled: z.boolean(),
    /** Minute of the UTC day the reminder fires, `0..1439`, or `null` while no
     *  hour has been chosen. NOT local — see the file docblock. */
    reminderUtcMinute: z
        .int()
        .min(0)
        .max(MINUTES_PER_DAY - 1)
        .nullable(),
    /** The LOCAL weekdays the reminder fires on, `0` Sunday; `null` or
     *  absent is every day, `[]` is refused. See {@link reminderWeekdays}. */
    reminderWeekdays,
    /** The morning-after soreness check-in (`D100`). On by default. */
    sorenessEnabled: z.boolean(),
    /** The two-minutes-after-the-session summary (`D100`). On by default. */
    summaryEnabled: z.boolean(),
    /** The push for a trainer reply nobody watched arrive (`D192`). On by
     *  default; ABSENT keeps the stored value, so a client that predates it
     *  cannot switch it back on by saving its other toggles. */
    trainerReplyEnabled: z.boolean().optional(),
    /** The DEVICE's current UTC offset in minutes, east of UTC positive.
     *  `new Date().getTimezoneOffset() * -1` on the client. */
    utcOffsetMinutes: z
        .int()
        .min(UTC_OFFSET_MINUTES_MIN)
        .max(UTC_OFFSET_MINUTES_MAX),
})
    .refine((value) => !value.reminderEnabled || value.reminderUtcMinute !== null, {
    error: "reminderUtcMinute is required when reminderEnabled is true — a reminder with no hour would never be selected by the per-minute job and would read as a broken toggle.",
    path: ["reminderUtcMinute"],
});
/**
 * What `GET` answers and what `PUT` echoes: the same shape plus when it was
 * last written.
 *
 * `z.object` rather than `strictObject` on the way out, like every other output
 * here — a client must not break when the server starts saying more.
 *
 * `updatedAt` is not decoration. It is what lets the app decide whether its own
 * cached copy or the server's is newer after an offline edit, which is a real
 * case for a screen with four toggles and no conflict UI.
 */
export const notificationPreferencesOutput = z.object({
    reminderEnabled: z.boolean(),
    reminderUtcMinute: z
        .int()
        .min(0)
        .max(MINUTES_PER_DAY - 1)
        .nullable(),
    /** Ascending, or `null` for every day. The server always sends it; it is
     *  optional here only so a client keeps decoding a server that predates it. */
    reminderWeekdays,
    sorenessEnabled: z.boolean(),
    summaryEnabled: z.boolean(),
    /** Always sent; optional so a client keeps decoding an older server. */
    trainerReplyEnabled: z.boolean().optional(),
    utcOffsetMinutes: z
        .int()
        .min(UTC_OFFSET_MINUTES_MIN)
        .max(UTC_OFFSET_MINUTES_MAX),
    updatedAt: z.iso.datetime(),
});
//# sourceMappingURL=preferences.js.map